import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "overlay", "content", "calendar", "emptyState"]
  static values = { url: String }

  connect() {
    // Bind escape key handler
    this.handleEscape = this.handleEscape.bind(this)
    this.calendar = null
    this.interactions = null
  }

  // Interactions are fetched lazily on first open so the contacts index
  // doesn't pay for calendar data on every page load
  async loadInteractionsData() {
    if (this.interactions) return

    try {
      const response = await fetch(this.urlValue, { headers: { Accept: 'application/json' } })
      this.interactions = response.ok ? await response.json() : []
    } catch (error) {
      console.error('Error loading calendar data:', error)
      this.interactions = []
    }
  }

  async open() {
    this.modalTarget.classList.remove('hidden')
    this.modalTarget.classList.add('contacts__calendar-modal--active')

    // Add escape key listener
    document.addEventListener('keydown', this.handleEscape)

    // Prevent body scroll
    document.body.style.overflow = 'hidden'

    // Focus management
    this.contentTarget.focus()

    // Initialize calendar once its data has arrived
    await this.loadInteractionsData()

    if (this.hasEmptyStateTarget) {
      const hasUpcoming = this.interactions.some(interaction => !interaction.is_past)
      this.emptyStateTarget.style.display = hasUpcoming ? 'none' : ''
    }

    this.initializeCalendar()
  }

  close() {
    this.modalTarget.classList.add('hidden')
    this.modalTarget.classList.remove('contacts__calendar-modal--active')
    
    // Remove escape key listener
    document.removeEventListener('keydown', this.handleEscape)
    
    // Restore body scroll
    document.body.style.overflow = ''
    
    // Destroy calendar when modal closes
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  initializeCalendar() {
    console.log('Initializing calendar')
    console.log('FullCalendar available:', typeof window.FullCalendar)
    
    if (this.calendar) {
      this.calendar.destroy()
    }

    // Convert interactions to FullCalendar events
    const events = this.formatEventsForCalendar(this.interactions || [])
    console.log('Calendar events:', events)

    this.calendar = new window.FullCalendar.Calendar(this.calendarTarget, {
      initialView: 'dayGridMonth',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth'
      },
      events: events,
      eventClick: this.handleEventClick.bind(this),
      eventDisplay: 'block',
      dayMaxEvents: 3,
      moreLinkClick: 'popover',
      height: 'auto',
      aspectRatio: 1.5,
      eventClassNames: 'contacts__calendar-event',
      eventDidMount: this.styleEvent.bind(this)
    })

    this.calendar.render()
  }

  formatEventsForCalendar(interactions) {
    return interactions.map(interaction => {
      const contact = interaction.contact
      const icon = this.getInteractionIcon(interaction.interaction_type)
      const title = `${icon} ${contact.current_name} - ${interaction.interaction_type}`
      const isPast = interaction.is_past
      
      return {
        id: interaction.id,
        title: title,
        start: interaction.next_action_date,
        backgroundColor: isPast ? this.getPastEventColor() : this.getEventColor(interaction.interaction_type),
        borderColor: isPast ? this.getPastEventColor() : this.getEventColor(interaction.interaction_type),
        textColor: isPast ? '#6b7280' : '#ffffff',
        className: isPast ? 'contacts__calendar-event--past' : 'contacts__calendar-event',
        extendedProps: {
          contactId: contact.id,
          contactName: contact.current_name,
          contactCompany: contact.current_company,
          interactionType: interaction.interaction_type,
          notes: interaction.notes,
          contactUrl: contact.url,
          isPast: isPast
        }
      }
    })
  }

  getInteractionIcon(interactionType) {
    const icons = {
      'call': '📞',
      'email': '📧',
      'meeting': '🤝',
      'follow_up': '📋',
      'default': '📅'
    }
    return icons[interactionType] || icons.default
  }

  getEventColor(interactionType) {
    // Use green color scheme to match contacts view upcoming events
    const colors = {
      'call': '#10b981',      // Green (matches contact interactions)
      'email': '#10b981',     // Green  
      'meeting': '#10b981',   // Green
      'follow_up': '#10b981', // Green
      'default': '#10b981'    // Green
    }
    return colors[interactionType] || colors.default
  }

  getPastEventColor() {
    return '#9ca3af' // Light gray for past events
  }

  styleEvent(info) {
    // Add custom styling to event elements
    const element = info.el
    const isPast = info.event.extendedProps.isPast
    
    element.style.cursor = 'pointer'
    element.style.borderRadius = '4px'
    element.style.fontSize = '12px'
    element.style.padding = '2px 4px'
    element.style.fontWeight = '500'
    
    if (isPast) {
      element.style.backgroundColor = '#9ca3af'
      element.style.borderColor = '#9ca3af'
      element.style.color = '#6b7280'
      element.style.opacity = '0.7'
    } else {
      element.style.backgroundColor = '#10b981'
      element.style.borderColor = '#10b981'
      element.style.color = '#ffffff'
    }
  }

  handleEventClick(info) {
    const props = info.event.extendedProps
    
    // Create a tooltip or modal with event details
    this.showEventDetails({
      title: info.event.title,
      date: info.event.start,
      contactName: props.contactName,
      contactCompany: props.contactCompany,
      interactionType: props.interactionType,
      notes: props.notes,
      contactUrl: props.contactUrl
    })
  }

  showEventDetails(eventData) {
    const backdrop = document.createElement('div')
    backdrop.className = 'contacts__event-popup-backdrop'

    const icon = this.getInteractionIcon(eventData.interactionType)
    const formattedDate = new Date(eventData.date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    const isPast = eventData.date < new Date()
    const statusIndicator = isPast ? '⏰ Past' : '🌱 Upcoming'

    backdrop.innerHTML = `
      <div class="contacts__event-popup" role="dialog" aria-modal="true">
        <div class="contacts__event-popup-header">
          <span class="contacts__event-popup-status ${isPast ? 'contacts__event-popup-status--past' : 'contacts__event-popup-status--upcoming'}">${statusIndicator}</span>
          <button type="button" class="contacts__event-popup-close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="contacts__event-popup-body">
          <h4 class="contacts__event-popup-title">${icon} ${eventData.contactName}</h4>
          ${eventData.contactCompany ? `<p class="contacts__event-popup-company">📍 ${eventData.contactCompany}</p>` : ''}
          <span class="contacts__event-popup-type ${isPast ? 'contacts__event-popup-type--past' : ''}">${eventData.interactionType.replace('_', ' ').toUpperCase()}</span>
          <p class="contacts__event-popup-date">📅 ${formattedDate}</p>
          ${eventData.notes ? `
            <div class="contacts__event-popup-notes">
              <span class="contacts__event-popup-notes-label">Notes from this interaction</span>
              <p class="contacts__event-popup-notes-text">${eventData.notes}</p>
            </div>
          ` : ''}
        </div>
        <div class="contacts__event-popup-footer">
          <button type="button" class="button button--outline contacts__event-popup-cancel">Close</button>
          <a href="${eventData.contactUrl}" class="button button--primary" data-turbo-frame="_top">View Contact</a>
        </div>
      </div>
    `

    const close = () => backdrop.remove()
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close()
    })
    backdrop.querySelector('.contacts__event-popup-close').addEventListener('click', close)
    backdrop.querySelector('.contacts__event-popup-cancel').addEventListener('click', close)

    document.body.appendChild(backdrop)
  }

  disconnect() {
    // Cleanup when controller disconnects
    document.removeEventListener('keydown', this.handleEscape)
    document.body.style.overflow = ''
    
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }
}